import { useEffect, useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { useWeeklyObjectivesStore } from "../../stores/use-weekly-objectives-store"
import WeeklyObjectivesBoard from "./weekly-objectives-board"
import WeeklyObjectivesDisplay from "./weekly-objectives-display"
import FilterWeeklyObjectiveByCourse from "../weekly-objectives-filters/filter-weekly-objective-by-course"
import type { WeeklyOfWeekly } from "../../entities/weekly-objectives/weekly-objectives"
import { isWithinInterval, toDate } from "date-fns"
import useCoursesInfo from "@/modules/courses/hooks/use-courses-info"
import type { Course } from "@/modules/courses/entities/courses"
import { PRODUCT_TOUR_STEPS } from "@/modules/product-tour/constants/steps"
import useGetCalendar from "@/modules/calendar/queries/use-get-calendar"
import extractISODate from "@/modules/calendar/utils/extract-iso-date"
import { startOfWeek, endOfWeek, addWeeks } from "date-fns"

interface WeeklyObjectivesProps {
  activeCourses: Course[]
  setActiveCourses: React.Dispatch<React.SetStateAction<Course[]>>
}

export default function WeeklyObjectives({
  activeCourses,
  setActiveCourses,
}: WeeklyObjectivesProps) {
  const { coursesInfo, hasCourses } = useCoursesInfo()
  const [openWeeklyObjective, setOpenWeeklyObjective] = useState(false)
  const [selectedColumn, setSelectedColumn] = useState<WeeklyOfWeekly>()
  const {
    weeklyObjectives,
    currentWeekNumber,
    selectedWeeklyObjective,
    setSelectedWeeklyObjective,
    setCurrentWeekNumber,
  } = useWeeklyObjectivesStore()
  const [isDragging, setIsDragging] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const scrollAttempted = useRef(false)
  const initialScrollDone = useRef(false)

  // Get user events for overload calculation
  const today = new Date()
  const weekStart = startOfWeek(today, { weekStartsOn: 1 })
  const weekEnd = endOfWeek(addWeeks(today, 10), { weekStartsOn: 1 }) // Get events for current and next 10 weeks

  const eventsQuery = useGetCalendar({
    startDate: extractISODate(weekStart),
    endDate: extractISODate(weekEnd),
    category: "study_time", // Only get study time events
  })

  const userEvents = eventsQuery.data?.data.description || []

  // Reset the scroll flags when the component is unmounted
  useEffect(() => {
    return () => {
      scrollAttempted.current = false
      initialScrollDone.current = false
    }
  }, [])

  // Set up visibility observer
  useEffect(() => {
    if (containerRef.current) {
      const observer = new IntersectionObserver(
        (entries) => {
          const [entry] = entries
          setIsVisible(entry.isIntersecting)
        },
        { threshold: 0.01 },
      )

      observer.observe(containerRef.current)

      return () => {
        if (containerRef.current) {
          observer.unobserve(containerRef.current)
        }
      }
    }
  }, [])

  // Calculate current week index
  useEffect(() => {
    if (weeklyObjectives.length > 0) {
      const today = new Date()

      const weekIndex = weeklyObjectives.findIndex((week) => {
        const start = toDate(week.startDate)
        const end = toDate(week.endDate)
        return isWithinInterval(today, { start, end })
      })

      if (weekIndex !== -1) {
        setCurrentWeekNumber(weekIndex)
      } else {
        // If no current week found, find the closest future week
        const futureWeekIndex = weeklyObjectives.findIndex((week) => {
          const start = toDate(week.startDate)
          return start > today
        })
        if (futureWeekIndex !== -1) {
          setCurrentWeekNumber(futureWeekIndex > 0 ? futureWeekIndex - 1 : 0)
        } else {
          // If no future week, use the last week
          setCurrentWeekNumber(weeklyObjectives.length - 1)
        }
      }
    }
  }, [weeklyObjectives])

  // Scroll to current week when conditions are met
  useEffect(() => {
    if (
      currentWeekNumber !== -1 &&
      scrollRef.current &&
      isVisible &&
      weeklyObjectives.length > 0 &&
      !initialScrollDone.current
    ) {
      const scrollToCurrentWeek = () => {
        if (scrollRef.current) {
          const columnWidth = 315 // Width of each column
          const targetPosition = currentWeekNumber * columnWidth
          // Adjust to position the current week at the left edge of the viewport
          scrollRef.current.scrollLeft = targetPosition
          initialScrollDone.current = true
          scrollAttempted.current = true
        }
      }

      // Add a delay to ensure DOM is ready
      const timeoutId = setTimeout(scrollToCurrentWeek, 500)
      return () => clearTimeout(timeoutId)
    }
  }, [currentWeekNumber, isVisible, weeklyObjectives.length])

  // Reset scroll flags if weekly objectives change significantly
  useEffect(() => {
    if (weeklyObjectives.length > 0 && !scrollAttempted.current) {
      initialScrollDone.current = false
    }
  }, [weeklyObjectives.length])

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const slider = scrollRef.current
    if (isDragging) return
    if (!slider) return

    slider.dataset.isDragging = "true"
    slider.dataset.startX = e.pageX.toString()
    slider.dataset.scrollLeft = slider.scrollLeft.toString()
  }

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const slider = scrollRef.current
    if (!slider || slider.dataset.isDragging !== "true") return

    const startX = Number(slider.dataset.startX || 0)
    const scrollLeft = Number(slider.dataset.scrollLeft || 0)
    const xDiff = e.pageX - startX

    slider.scrollLeft = scrollLeft - xDiff
  }

  const handleMouseUp = () => {
    const slider = scrollRef.current
    if (slider) slider.dataset.isDragging = "false"
  }

  const handleIsDragging = (dragging: boolean) => {
    setIsDragging(dragging)
  }

  const handleGoToCurrentWeekly = () => {
    if (scrollRef.current && currentWeekNumber !== -1) {
      const columnWidth = 315
      const scrollLeft = currentWeekNumber * columnWidth
      scrollRef.current.scrollTo({ left: scrollLeft, behavior: "smooth" })
    } else if (scrollRef.current) {
      scrollRef.current.scrollTo({ left: 0, behavior: "smooth" })
    }
  }

  const handleOnClickNextweek = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -600, behavior: "smooth" })
    }
  }

  const handleOnClickBacktweek = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 600, behavior: "smooth" })
    }
  }

  const handleDisplayObjective = (column: WeeklyOfWeekly, id: string) => () => {
    setSelectedColumn(column)
    setOpenWeeklyObjective(true)
    setSelectedWeeklyObjective(id)
  }

  const handleOnChangeFilterCourses = (newActiveCourses: Course[]) => {
    setActiveCourses(newActiveCourses)
  }

  return (
    <div
      className="flex flex-col gap-4 overflow-hidden"
      id={PRODUCT_TOUR_STEPS.PLANNER_MOVE_OBJECTIVE}
    >
      {hasCourses && selectedColumn ? (
        <WeeklyObjectivesDisplay
          column={selectedColumn}
          open={openWeeklyObjective}
          courses={coursesInfo.courses}
          onOpenChange={setOpenWeeklyObjective}
          objective={selectedWeeklyObjective}
        />
      ) : null}
      <div className="flex flex-row justify-between py-4 pl-1 pr-6">
        <p className="self-center text-xl font-bold">Objetivos semanales</p>
        <div className="hidden h-full flex-row gap-2 self-center md:flex">
          {hasCourses && (
            <FilterWeeklyObjectiveByCourse
              courses={coursesInfo.courses}
              onChange={handleOnChangeFilterCourses}
            />
          )}
          <Button variant="ghost" className="text-md" onClick={handleGoToCurrentWeekly}>
            Ir a semana actual
          </Button>
          <div className="flex items-center gap-2">
            <button
              onClick={handleOnClickNextweek}
              className="rounded-full p-1 hover:bg-neutral-100 disabled:opacity-50 disabled:hover:bg-transparent"
            >
              <ChevronLeft size={26} />
            </button>
            <button
              onClick={handleOnClickBacktweek}
              className="rounded-full p-1 hover:bg-neutral-100 disabled:opacity-50 disabled:hover:bg-transparent"
            >
              <ChevronRight size={26} />
            </button>
          </div>
        </div>
      </div>
      <div ref={containerRef} className="flex flex-col">
        <div
          ref={scrollRef}
          className="no-scrollbar no-scrollbar flex overflow-x-auto"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseUp}
          onMouseUp={handleMouseUp}
        >
          <WeeklyObjectivesBoard
            isDragging={isDragging}
            setIsDragging={handleIsDragging}
            displayObjective={handleDisplayObjective}
            activeCourses={activeCourses}
            userEvents={userEvents}
          />
        </div>
      </div>
    </div>
  )
}
